import React, { useState } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import useMobileLayout from 'Hooks/useMobileLayout';
import ManagedAccountActivityInfo
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/ManagedAccountActivitySummary/ManagedAccountActivityInfo';
import ManagedAccountActivitySummaryChart
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/ManagedAccountActivitySummary/ManagedAccountActivitySummaryChart';
import Typography from 'Components/UI/Typography';
import { DATE_RANGE_CUSTOM } from 'Consts/dateRange';
import useManagedAccountPageEventLogger, {
  logMetaQueryFieldGroupChange,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/managedAccountPage.eventLogging';
import { useSnackbarContext } from 'Context/SnackbarContext';
import MetaQueryFieldGroup from './MetaQueryFieldGroup';

const useStyles = buildUseStyles('ManagedAccountActivitySummary', (theme) => ({
  activitySummaryControls: {
    marginBottom: theme.spacing(2),
  },
  activitySummaryData: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(5),
    justifyContent: 'center',
  },
  activitySummaryChart: {
    minHeight: 320,
    flex: `1 1 ${theme.dimensions.lg}px`,
    maxWidth: '100%',
  },
  activitySummaryInfo: {
    flex: `0 0 ${theme.dimensions.md}px`,
    maxWidth: '100%',
  },
}));

function useManagedAccountActivitySummary(activitySummary) {
  const [resource, setResource] = useState(activitySummary);

  return {
    resource,
    setResource,
  };
}

const metaQueryOverride = ({ resource, metaQueryValues }) => fr.metaOverride().name('activitySummaryMetaQueryOverrideSet')
  .overrideAll({
    hidden: true,
  }, { ordinal: 200 })
  .nameOverride('datePeriod', {
    hidden: false,
    default: resource?.link('self')?.param('datePeriod') || DATE_RANGE_CUSTOM,
    required: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
  }, { ordinal: 201 })
  .nameOverride('startDate', {
    hidden: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    default: resource.attr('startingDate'),
    required: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
  }, { ordinal: 201 })
  .nameOverride('endDate', {
    hidden: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    default: resource.attr('endingDate'),
    required: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
  }, { ordinal: 201 });


export default function ManagedAccountActivitySummary({ activitySummary }) {
  const { displayErrorMessage } = useSnackbarContext();

  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });
  const { logActivitySummaryFiltersEvent } = useManagedAccountPageEventLogger();

  const { resource, setResource } = useManagedAccountActivitySummary(activitySummary);

  async function handleNewQueryLink(link) {
    try {
      const [data] = await link.get();
      setResource(data);
    } catch (e) {
      displayErrorMessage(e, (e.errors && e.errors[0]?.detail) || 'There was a problem loading the chart data.');
    }
  }

  function logActivitySummaryControlChange(values, field, newValue) {
    logMetaQueryFieldGroupChange(values, field, newValue, logActivitySummaryFiltersEvent);
  }

  if (!activitySummary) return <Box><Typography>No Account Activity Summary</Typography></Box>;

  return (
    <>
      <Box className={classes.activitySummaryControls}>
        <MetaQueryFieldGroup
          test="managed-account-activity-summary-controls"
          resource={resource}
          metaQueryName={fr.queries.getAccountActivitySummaries.name}
          metaQueryOverride={metaQueryOverride}
          onNewLink={handleNewQueryLink}
          onValueChange={logActivitySummaryControlChange}
        />
      </Box>
      <Box className={classes.activitySummaryData}>
        <Box className={classes.activitySummaryChart} test="managed-account-activity-summary-chart">
          <ManagedAccountActivitySummaryChart activitySummary={resource} />
        </Box>
        <Box className={classes.activitySummaryInfo} test="managed-account-activity-summary-info">
          <ManagedAccountActivityInfo activitySummary={resource} />
        </Box>
      </Box>
    </>
  );
}
ManagedAccountActivitySummary.propTypes = {
  activitySummary: PropTypes.instanceOf(fr.Resource).isRequired,
};
